import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Button, FormControl, FormLabel, Input, Table, Thead, Tbody, Tr, Th, Td, IconButton, useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, ModalFooter, useDisclosure, VStack, HStack, Badge, Text, Link, Alert, AlertIcon, AlertTitle, Code, Tooltip, InputGroup, InputLeftElement
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { Pagination, usePagination } from '../shared/Pagination';
import { EditIcon, DeleteIcon, AddIcon, ExternalLinkIcon, ViewIcon, LockIcon, CopyIcon } from '@chakra-ui/icons';
import { supabase } from '../../supabaseClient';

import { getAuthHeaders } from '../../utils/apiAuth';
const DoctorManagement = () => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isSecretKeyModalOpen, onOpen: onSecretKeyModalOpen, onClose: onSecretKeyModalClose } = useDisclosure();
  const [selectedDoctorForKey, setSelectedDoctorForKey] = useState(null);
  const [newSecretKey, setNewSecretKey] = useState('');
  const [generatingKey, setGeneratingKey] = useState(false);
  const toast = useToast();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    npi_number: '',
    clinic_name: '',
    address: '',
    hours: {
      Monday: { start: '', end: '' },
      Tuesday: { start: '', end: '' },
      Wednesday: { start: '', end: '' },
      Thursday: { start: '', end: '' },
      Friday: { start: '', end: '' },
      Saturday: { start: '', end: '' },
      Sunday: { start: '', end: '' },
    },
  });

  const fetchDoctors = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('doctors')
      .select(`
        *,
        short_urls (
          short_code,
          is_active
        )
      `)
      .order('created_at', { ascending: false });
    if (error) {
      toast({ title: 'Error fetching doctors', description: error.message, status: 'error' });
    } else {
      setDoctors(data || []);
    }
    setLoading(false);
  }, [toast]);

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleHoursChange = (day, field, value) => {
    setFormData((prev) => ({
      ...prev,
      hours: {
        ...prev.hours,
        [day]: {
          ...prev.hours[day],
          [field]: value,
        },
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (selectedDoctor) {
        // Update
        const { error } = await supabase
          .from('doctors')
          .update({
            ...formData,
            hours: formData.hours,
          })
          .eq('id', selectedDoctor.id);
        if (error) throw error;
        toast({ title: 'Doctor updated', status: 'success' });
      } else {
        // Add new doctor
        const { data: newDoctor, error: insertError } = await supabase
          .from('doctors')
          .insert([{ ...formData, hours: formData.hours }])
          .select()
          .single();
        
        if (insertError) throw insertError;

        // Generate short URL for the new doctor
        try {
          const { data: codeData, error: codeError } = await supabase
            .rpc('generate_short_code');

          if (codeError) throw codeError;

          const shortCode = codeData;

          // Insert the short URL
          const { error: shortUrlError } = await supabase
            .from('short_urls')
            .insert({
              short_code: shortCode,
              doctor_id: newDoctor.id,
              expires_at: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString() // 1 year expiration
            });

          if (shortUrlError) {
            console.error('Error creating short URL:', shortUrlError);
            // Don't throw error - doctor creation was successful
          } else {
            toast({ 
              title: 'Doctor added with short URL', 
              description: `Short URL created: ${window.location.origin}/${shortCode}`,
              status: 'success',
              duration: 5000
            });
          }
        } catch (shortUrlError) {
          console.error('Error generating short URL:', shortUrlError);
          toast({ title: 'Doctor added (short URL creation failed)', status: 'warning' });
        }
      }
      fetchDoctors();
      onClose();
      setFormData({ full_name: '', email: '', phone: '', npi_number: '', clinic_name: '', address: '', hours: {
        Monday: { start: '', end: '' },
        Tuesday: { start: '', end: '' },
        Wednesday: { start: '', end: '' },
        Thursday: { start: '', end: '' },
        Friday: { start: '', end: '' },
        Saturday: { start: '', end: '' },
        Sunday: { start: '', end: '' },
      } });
      setSelectedDoctor(null);
    } catch (error) {
      toast({ title: 'Error', description: error.message, status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (doctor) => {
    setSelectedDoctor(doctor);
    setFormData({
      full_name: doctor.full_name || '',
      email: doctor.email || '',
      phone: doctor.phone || '',
      npi_number: doctor.npi_number || '',
      clinic_name: doctor.clinic_name || '',
      address: doctor.address || '',
      hours: doctor.hours || {
        Monday: { start: '', end: '' },
        Tuesday: { start: '', end: '' },
        Wednesday: { start: '', end: '' },
        Thursday: { start: '', end: '' },
        Friday: { start: '', end: '' },
        Saturday: { start: '', end: '' },
        Sunday: { start: '', end: '' },
      },
    });
    onOpen();
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Are you sure you want to delete this doctor? This action cannot be undone.');
    if (!confirmed) return;
    setLoading(true);
    const { error } = await supabase.from('doctors').delete().eq('id', id);
    if (error) {
      toast({ title: 'Error deleting doctor', description: error.message, status: 'error' });
    } else {
      toast({ title: 'Doctor deleted', status: 'success' });
      fetchDoctors();
    }
    setLoading(false);
  };

  const handleOpenSecretKey = (doctor) => {
    setSelectedDoctorForKey(doctor);
    setNewSecretKey(doctor.secret_key || '');
    onSecretKeyModalOpen();
  };

  const setSecretKey = async (doctorId, secretKey) => {
    const trimmedKey = (secretKey || '').trim();
    if (!trimmedKey) {
      toast({ title: 'Key required', description: 'Please enter a secret key.', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    setGeneratingKey(true);
    const apiBase = process.env.REACT_APP_API_URL || '';
    try {
      const response = await fetch(`${apiBase}/api/set-doctor-secret-key`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ doctorId, secretKey: trimmedKey }),
      });
      let result;
      try {
        result = await response.json();
      } catch {
        throw new Error('API not available. For local dev run "vercel dev" or set REACT_APP_API_URL to your deployed URL.');
      }
      if (!response.ok) throw new Error(result.error || 'Failed to save key');
      setDoctors(prev => prev.map(doctor => doctor.id === doctorId ? { ...doctor, secret_key: trimmedKey } : doctor));
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      if (adminUser?.name) {
        await supabase.from('admin_activity_log').insert({
          action: 'Updated Doctor Secret Key',
          username: adminUser.name,
          details: `Doctor ID: ${doctorId}`,
          created_at: new Date().toISOString(),
        });
      }
      toast({ title: 'Secret key updated', description: result.message || 'Doctor portal key has been saved.', status: 'success', duration: 3000, isClosable: true });
      onSecretKeyModalClose();
      setNewSecretKey('');
    } catch (error) {
      toast({ title: 'Error updating secret key', description: error.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setGeneratingKey(false);
    }
  };

  const copyShortUrl = async (doctorId) => {
    try {
      // Fetch the existing short URL for this doctor
      const { data: existingUrl, error } = await supabase
        .from('short_urls')
        .select('short_code')
        .eq('doctor_id', doctorId)
        .eq('is_active', true)
        .single();

      if (error || !existingUrl) {
        toast({ 
          title: 'No short URL found', 
          description: 'Please create the doctor again to generate a short URL.', 
          status: 'error' 
        });
        return;
      }

      const shortUrl = `${window.location.origin}/${existingUrl.short_code}`;
      await navigator.clipboard.writeText(shortUrl);
      toast({ 
        title: 'Short URL copied!', 
        description: `Share this URL: ${shortUrl}`, 
        status: 'success',
        duration: 5000 
      });
    } catch (error) {
      toast({ 
        title: 'Error copying short URL', 
        description: error.message, 
        status: 'error' 
      });
    }
  };

  const filteredDoctors = useMemo(() => {
    if (!searchQuery.trim()) return doctors;
    const q = searchQuery.toLowerCase();
    return doctors.filter(d =>
      d.full_name?.toLowerCase().includes(q) ||
      d.email?.toLowerCase().includes(q) ||
      d.phone?.toLowerCase().includes(q) ||
      d.clinic_name?.toLowerCase().includes(q)
    );
  }, [doctors, searchQuery]);

  const [paginatedDoctors, paginationProps] = usePagination(filteredDoctors, 25);

  return (
    <Box p={6}>
      <HStack justify="space-between" mb={4}>
        <Box as="h2" fontSize="2xl" fontWeight="bold">Doctors</Box>
        <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={() => { setSelectedDoctor(null); setFormData({ full_name: '', email: '', phone: '', npi_number: '', clinic_name: '', address: '', hours: {
          Monday: { start: '', end: '' },
          Tuesday: { start: '', end: '' },
          Wednesday: { start: '', end: '' },
          Thursday: { start: '', end: '' },
          Friday: { start: '', end: '' },
          Saturday: { start: '', end: '' },
          Sunday: { start: '', end: '' },
        } }); onOpen(); }}>Add Doctor</Button>
      </HStack>
      <InputGroup mb={4} maxW="400px">
        <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" /></InputLeftElement>
        <Input
          placeholder="Search doctors by name, email, phone, or clinic..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </InputGroup>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Phone</Th>
            <Th>Client Name</Th>
            <Th>Secret Key</Th>
            <Th>Last Accessed</Th>
            <Th>Agreement Status</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {paginatedDoctors.map((doctor) => (
            <Tr key={doctor.id}>
              <Td>{doctor.full_name}</Td>
              <Td>{doctor.email}</Td>
              <Td>{doctor.phone}</Td>
              <Td>{doctor.clinic_name}</Td>
              <Td>
                {doctor.secret_key ? (
                  <HStack spacing={1}>
                    <Code fontSize="xs" maxW="120px" isTruncated>
                      {doctor.secret_key}
                    </Code>
                    <Tooltip label="Copy Secret Key" hasArrow>
                      <IconButton
                        icon={<CopyIcon />}
                        size="xs"
                        variant="ghost"
                        onClick={() => {
                          navigator.clipboard.writeText(doctor.secret_key);
                          toast({ title: 'Secret key copied', status: 'success', duration: 1500 });
                        }}
                        aria-label="Copy secret key"
                      />
                    </Tooltip>
                  </HStack>
                ) : (
                  <Text fontSize="xs" color="gray.400">No key set</Text>
                )}
              </Td>
              <Td>
                {doctor.last_login_at ? (
                  <VStack align="start" spacing={0}>
                    <Text fontSize="sm">{new Date(doctor.last_login_at).toLocaleDateString()}</Text>
                    <Text fontSize="xs" color="gray.500">{new Date(doctor.last_login_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Text>
                  </VStack>
                ) : (
                  <Text fontSize="xs" color="gray.400">Never</Text>
                )}
              </Td>
              <Td>
                {doctor.agreement_signed ? (
                  <VStack align="start" spacing={1}>
                    <Badge colorScheme="green">Signed</Badge>
                    <Text fontSize="xs" color="gray.500">
                      {new Date(doctor.agreement_signed_at).toLocaleDateString()}
                    </Text>
                    {doctor.agreement_signature_url && (
                      <VStack align="start" spacing={1}>
                        <Link
                          href={doctor.agreement_signature_url}
                          target="_blank"
                          color="blue.500"
                          fontSize="xs"
                        >
                          View Signature
                        </Link>
                        {doctor.agreement_pdf_url && (
                          <Link
                            href={doctor.agreement_pdf_url}
                            target="_blank"
                            color="green.500"
                            fontSize="xs"
                          >
                            Download PDF
                          </Link>
                        )}
                      </VStack>
                    )}
                  </VStack>
                ) : (
                  <VStack align="start" spacing={1}>
                    <Badge colorScheme="red">Not Signed</Badge>
                    {doctor.short_urls && doctor.short_urls.length > 0 ? (
                      <VStack align="start" spacing={1}>
                        <Text fontSize="xs" color="gray.500">
                          /{doctor.short_urls[0].short_code}
                        </Text>
                        <Button
                          size="xs"
                          colorScheme="blue"
                          variant="outline"
                          onClick={() => copyShortUrl(doctor.id)}
                          leftIcon={<ExternalLinkIcon />}
                        >
                          Copy Link
                        </Button>
                      </VStack>
                    ) : (
                      <Text fontSize="xs" color="red.500">
                        No short URL (recreate doctor)
                      </Text>
                    )}
                  </VStack>
                )}
              </Td>
              <Td>
                <HStack spacing={2} flexWrap="wrap">
                  <IconButton icon={<EditIcon />} onClick={() => handleEdit(doctor)} aria-label="Edit" size="sm" />
                  <IconButton icon={<DeleteIcon />} colorScheme="red" onClick={() => handleDelete(doctor.id)} aria-label="Delete" size="sm" />
                  <Button
                    leftIcon={<LockIcon />}
                    size="sm"
                    variant="outline"
                    colorScheme={doctor.secret_key ? 'orange' : 'blue'}
                    onClick={() => handleOpenSecretKey(doctor)}
                  >
                    {doctor.secret_key ? 'Update Key' : 'Set Key'}
                  </Button>
                  {doctor.email && (
                    <Button
                      as={Link}
                      href={`/doctor/login?email=${encodeURIComponent(doctor.email)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="sm"
                      colorScheme="purple"
                      variant="outline"
                      leftIcon={<ExternalLinkIcon />}
                    >
                      View Portal
                    </Button>
                  )}
                  {doctor.agreement_signed && doctor.agreement_signature_url && (
                    <IconButton
                      icon={<ViewIcon />}
                      colorScheme="blue"
                      onClick={() => window.open(doctor.agreement_signature_url, '_blank')}
                      aria-label="View Signature"
                      size="sm"
                    />
                  )}
                  {doctor.agreement_signed && doctor.agreement_pdf_url && (
                    <IconButton
                      icon={<ExternalLinkIcon />}
                      colorScheme="green"
                      onClick={() => window.open(doctor.agreement_pdf_url, '_blank')}
                      aria-label="Download PDF"
                      size="sm"
                    />
                  )}
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      <Pagination {...paginationProps} />
      <Modal isOpen={isOpen} onClose={() => { onClose(); setSelectedDoctor(null); setFormData({ full_name: '', email: '', phone: '', npi_number: '', clinic_name: '', address: '', hours: { Monday: { start: '', end: '' }, Tuesday: { start: '', end: '' }, Wednesday: { start: '', end: '' }, Thursday: { start: '', end: '' }, Friday: { start: '', end: '' }, Saturday: { start: '', end: '' }, Sunday: { start: '', end: '' } } }); }} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{selectedDoctor ? 'Edit Doctor' : 'Add Doctor'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <form onSubmit={handleSubmit}>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Full Name</FormLabel>
                  <Input name="full_name" value={formData.full_name} onChange={handleInputChange} />
                </FormControl>
                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <Input name="email" value={formData.email} onChange={handleInputChange} />
                </FormControl>
                <FormControl>
                  <FormLabel>Phone</FormLabel>
                  <Input name="phone" value={formData.phone} onChange={handleInputChange} />
                </FormControl>
                <FormControl>
                  <FormLabel>NPI Number</FormLabel>
                  <Input name="npi_number" value={formData.npi_number} onChange={handleInputChange} />
                </FormControl>
                <FormControl>
                  <FormLabel>Client Name (Clinic / Facility)</FormLabel>
                  <Input name="clinic_name" value={formData.clinic_name} onChange={handleInputChange} placeholder="Facility or client name (used on submissions)" />
                </FormControl>
                <FormControl>
                  <FormLabel>Address</FormLabel>
                  <Input name="address" value={formData.address} onChange={handleInputChange} />
                </FormControl>
                <Box w="100%" borderWidth={1} borderRadius="md" p={3} mt={2} mb={2}>
                  <FormLabel>Working Hours</FormLabel>
                  {Object.keys(formData.hours).map((day) => (
                    <HStack key={day} mb={1}>
                      <Box minW="80px">{day}</Box>
                      <Input
                        type="time"
                        value={formData.hours[day].start}
                        onChange={(e) => handleHoursChange(day, 'start', e.target.value)}
                        size="sm"
                        placeholder="Start"
                      />
                      <Box>-</Box>
                      <Input
                        type="time"
                        value={formData.hours[day].end}
                        onChange={(e) => handleHoursChange(day, 'end', e.target.value)}
                        size="sm"
                        placeholder="End"
                      />
                    </HStack>
                  ))}
                </Box>
                <ModalFooter px={0}>
                  <Button colorScheme="blue" mr={3} type="submit" isLoading={loading}>
                    {selectedDoctor ? 'Update' : 'Add'}
                  </Button>
                  <Button onClick={onClose}>Cancel</Button>
                </ModalFooter>
              </VStack>
            </form>
          </ModalBody>
          </ModalContent>
        </Modal>

        {/* Secret Key Modal */}
        <Modal
          isOpen={isSecretKeyModalOpen}
          onClose={() => { onSecretKeyModalClose(); setNewSecretKey(''); setSelectedDoctorForKey(null); }}
          size="md"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Set Secret Key for {selectedDoctorForKey?.full_name}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <Text fontSize="sm" color="gray.600">
                  {selectedDoctorForKey?.secret_key
                    ? 'Setting a new secret key will replace the current one. The doctor will need to use the new key for portal access.'
                    : 'Set a unique secret key for doctor portal access. The doctor will use this to sign in at their portal.'}
                </Text>
                {selectedDoctorForKey?.secret_key && (
                  <Box p={3} bg="orange.50" borderRadius="md" border="1px solid" borderColor="orange.200">
                    <Text fontSize="sm" color="orange.800" fontWeight="medium">
                      Current key will be replaced
                    </Text>
                  </Box>
                )}
                <FormControl isRequired>
                  <FormLabel>Secret Key</FormLabel>
                  <Input
                    type="password"
                    value={newSecretKey}
                    onChange={(e) => setNewSecretKey(e.target.value)}
                    placeholder="Enter a secure secret key"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newSecretKey.trim()) {
                        e.preventDefault();
                        setSecretKey(selectedDoctorForKey?.id, newSecretKey);
                      }
                    }}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Give this key to the doctor; they enter it on the portal login page.
                  </Text>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={() => { onSecretKeyModalClose(); setNewSecretKey(''); setSelectedDoctorForKey(null); }}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                isLoading={generatingKey}
                isDisabled={!newSecretKey.trim()}
                onClick={() => setSecretKey(selectedDoctorForKey?.id, newSecretKey)}
              >
                {selectedDoctorForKey?.secret_key ? 'Update Key' : 'Set Key'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    );
  };
  
  export default DoctorManagement;